<template>
  <div class="relative w-full h-full">
    <div class="text-2xl font-medium">
      {{ $t("Key People") }}
    </div>

    <Menu as="div" class="flex-shrink-0 absolute top-6 right-2">
      <MenuButton
        class="w-5 h-5 inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
        <EllipsisVertical class="w-5 h-5" aria-hidden="true" />
      </MenuButton>
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95">
        <MenuItems
          class="absolute z-50 mx-3 -ml-[160px] whitespace-nowrap origin-bottom-right dark:bg-dark-screen-background w-48 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
          <MenuItem v-slot="{ active }">
            <div
              :class="[
                active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                ' cursor-pointer group flex items-center px-4 py-2 '
              ]"
              @click="$emitter.emit(ALL_EMITTERS.EDIT_KEY_RECORDS, { record: component_record })">
              <Pencil class="mr-3 h-5 w-5" /> {{ $t("Edit List") }}
            </div>
          </MenuItem>

          <MenuItem v-slot="{ active }">
            <div
              :class="[
                active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                ' cursor-pointer group flex items-center px-4 py-2 '
              ]"
              @click="$emitter.emit(ALL_EMITTERS.EMPTY_KEY_RECORDS, { record: component_record })">
              <Trash2 class="mr-3 h-5 w-5" /> {{ $t("Empty List") }}
            </div>
          </MenuItem>
        </MenuItems>
      </transition>
    </Menu>

    <div class="w-full overflow-auto mt-4 h-[calc(100%-40px)]">
      <div v-if="!component_record?.key_records?.length" class="flex flex-wrap text-common-grey">
        {{
          $t(
            "Key people should include contacts that are particularly important, like for example a decision maker, influencer or power user."
          )
        }}
      </div>
      <div
        v-for="key_record in component_record?.key_records"
        :key="key_record?.id"
        class="flex cursor-pointer space-x-2 w-full group/tab mt-4"
        @click="$utils.Record(key_record).open()">
        <Avatar
          :radius="40"
          class="mt-1 mr-1"
          :hover-effect="true"
          :first-name="$utils.Record(key_record)?.first_name"
          :last-name="$utils.Record(key_record)?.last_name" />
        <div class="flex flex-col -space-y-1">
          <div class="text-lg font-medium">
            {{ $utils.Record(key_record).name }}
          </div>
          <div class="text-md text-common-grey font-normal capitalize">
            {{ key_record.type }}
          </div>
          <div class="flex w-full dark:border-dark-divider-border rounded-lg h-full py-1">
            <div class="flex flex-grow w-full">
              <div class="flex-1" />
              <div class="flex justify-start w-full pl-0 cursor-pointer">
                <span
                  v-for="i in 5"
                  :key="i"
                  @click.stop="
                    () => {
                      key_record.values['contact_relationship_strength_rs'] = i
                      handleUpdateStarValue(i, key_record)
                    }
                  ">
                  <svg
                    class="block w-5 h-5 mr-2"
                    :class="[
                      key_record?.values
                        ? key_record?.values['contact_relationship_strength_rs'] >= i
                          ? key_record?.values['contact_relationship_strength_rs'] >= 4
                            ? 'text-status-success-green'
                            : key_record?.values['contact_relationship_strength_rs'] >= 3
                              ? 'text-status-warning-yellow'
                              : 'text-status-error-red'
                          : 'text-gray-300'
                        : null
                    ]"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="!ml-auto mt-4 relative right-2 cursor-pointer" @click.stop="$emitter.emit('sendEmail', { record: key_record })">
          <Mail color="#2DA7D7" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from "@/components/shared/Avatar.vue"
import { Mail, Trash2, EllipsisVertical, Pencil } from "lucide-vue-next"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import { useLayoutItemSharedLogic } from "@/components/shared/pages/useLayoutItemSharedLogic.js"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: { Pencil, Avatar, Trash2, Mail, Menu, MenuButton, MenuItem, MenuItems, EllipsisVertical },
  props: {
    linkedRecord: null,
    connectTo: null
  },
  emits: [],
  data() {
    return {
      ALL_EMITTERS,
      component_record: null,
      record_error: null,
      loading: false,
      record_not_found: false
    }
  },
  created() {
    const { loading, record_error, component_record } = useLayoutItemSharedLogic(
      this.$props,
      this.postGetComponentRecord,
      this.registerListeners,
      this.unregisterListeners
    )
    this.loading = loading
    this.record_error = record_error
    this.component_record = component_record
    this.registerListeners()
  },
  methods: {
    registerListeners() {},
    unregisterListeners() {},
    postGetComponentRecord() {},
    handleUpdateStarValue(i, key_record) {
      this.$store
        .dispatch("data/updateRecord", {
          record_id: key_record.id,
          data: {
            values: { ...key_record.values, ["contact_relationship_strength_rs"]: i }
          }
        })
        .then(() => {
          this.$emitter.emit(ALL_EMITTERS.UPDATE_KEY_RECORDS_TIED_RECORD)
        })
    }
  }
}
</script>
